import React, { useState } from 'react';
import { FormHelperText, Grid } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { useBuilderContext, useField, useIsReadOnly } from '../../context';
import { getJoinWindowPickerError } from '../../helpers/enrollmentHelpers';

// BhiDateTimePicker has no minDateTime prop, so the ordering constraint
// (joinEndAt > joinStartAt) lives in the yup schema, not in the picker.
//
// `allowEmpty` matters: without it the picker fills an empty field with the next
// quarter-hour on mount. Both bounds would take the same timestamp, so a fresh
// enrollment campaign opened straight to "end must be after start" — and counted
// as unsaved before the user had touched anything.

const Bound: React.FC<{ label: string; name: 'joinStartAt' | 'joinEndAt' }> = ({ label, name }) => {
  const { value, error, onChange, onBlur } = useField(name);
  const readOnly = useIsReadOnly();
  const { campaignId } = useBuilderContext();
  const start = useField('joinStartAt').value;
  const [touched, setTouched] = useState(false);
  const [pickerError, setPickerError] = useState<string>();
  const displayedError = pickerError ?? error;

  const isCurrentMinute = (reason: string | null, next: Dayjs | null) =>
    reason === 'disablePast' && !!next && next.isValid() && next.isSame(dayjs(), 'minute');

  return (
    <Grid item xs={12} md={6}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {/* DemoContainer, not a plain Box: besides the 8px top padding it also
            carries `overflow: auto` and a 270px min-width on the picker's
            TextField, so a narrow column scrolls the field instead of squeezing
            it. */}
        <DemoContainer components={['DateTimePicker']} sx={{ mt: 0 }}>
          <DateTimePicker
            label={label}
            disablePast={name === 'joinStartAt' || touched}
            referenceDate={name === 'joinStartAt' ? dayjs() : undefined}
            minDateTime={name === 'joinEndAt' && start ? dayjs(start) : undefined}
            sx={{ width: '100%' }}
            slotProps={{ textField: { error: !!displayedError } }}
            ampm
            format="MM/DD/YYYY hh:mm A"
            closeOnSelect={false}
            value={value ? dayjs(value) : null}
            onError={(reason, next) => {
              if (name === 'joinStartAt' && (campaignId || isCurrentMinute(reason, next))) {
                setPickerError(undefined);
              } else {
                setPickerError(getJoinWindowPickerError(
                  name === 'joinStartAt' ? 'start' : 'end',
                  reason,
                ));
              }
            }}
            onChange={(next) => {
              setTouched(true);
              onChange(next && next.isValid() ? next.toISOString() : null);
              onBlur();
            }}
            disabled={readOnly}
          />
        </DemoContainer>
      </LocalizationProvider>
      <FormHelperText error={!!displayedError} sx={{ mt: 0, minHeight: 20 }}>
        {displayedError || ' '}
      </FormHelperText>
    </Grid>
  );
};

export const JoinWindowField: React.FC = () => (
  <Grid container spacing={2}>
    <Bound label="Join Window Start" name="joinStartAt" />
    <Bound label="Join Window End" name="joinEndAt" />
  </Grid>
);
